import React from 'react';
import styled from 'styled-components';
import { Button, Checkbox } from '@prosolve/common-components-ui';
import { FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import RangeEditor from './RangeEditor';
import { getEligibleQuestions } from './ConditionEditor';

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #565656;
  margin-bottom: 5px;
`

const SectionHint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #7e7e7e;
`

const QuestionBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 10px;
  border: 1px solid #dedede;
  border-radius: 5px;
`

const QuestionText = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #565656;
`

const WarningText = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  font-size: 12px;
  font-style: italic;
  color: #7e7e7e;
`

export default ({
  grade = {},
  questions = [],
  assessmentNames = [],
  modsByAssessment = {},
  currentAssessmentName,
  onChange,
  isReadOnly
}) => {
  const { correctAnswers = {}, ranges = [] } = grade;

  const eligibleQuestions = getEligibleQuestions(questions);

  const staleQuestionIds = Object.keys(correctAnswers).filter(questionId =>
    !eligibleQuestions.some(({ id }) => String(id) === String(questionId)));

  const onCorrectOptionToggled = (questionId, optionId) => {
    const key = String(questionId);
    const idStr = String(optionId);
    const currentIds = correctAnswers[key] || [];
    const updatedIds = currentIds.some(id => String(id) === idStr)
      ? currentIds.filter(id => String(id) !== idStr)
      : [...currentIds, idStr];

    const updatedCorrectAnswers = { ...correctAnswers };
    if (updatedIds.length) {
      updatedCorrectAnswers[key] = updatedIds;
    } else {
      // No correct options means the question is not graded — drop the key
      delete updatedCorrectAnswers[key];
    }
    onChange({ ...grade, correctAnswers: updatedCorrectAnswers })
  }

  const removeStaleQuestion = (questionId) => {
    const updatedCorrectAnswers = { ...correctAnswers };
    delete updatedCorrectAnswers[questionId];
    onChange({ ...grade, correctAnswers: updatedCorrectAnswers })
  }

  const onRangeChanged = (rangeIndex, updatedRange) => {
    const updatedRanges = [...ranges];
    updatedRanges[rangeIndex] = updatedRange;
    onChange({ ...grade, ranges: updatedRanges })
  }

  const onRangeDeleted = (rangeIndex) => {
    onChange({ ...grade, ranges: ranges.filter((r, i) => i !== rangeIndex) })
  }

  const addNewRange = () => {
    const newRange = {
      id: String(Date.now()),
      minPercent: '',
      maxPercent: '',
      followupAssessmentName: ''
    };
    onChange({ ...grade, ranges: [...ranges, newRange] })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
      <div>
        <SectionTitle>{'Correct Answers'}</SectionTitle>
        <SectionHint>
          {'Mark the correct answer option(s) for each question you want graded. A question is correct only when the student\'s selections exactly match. Questions with no marked options are not graded.'}
        </SectionHint>
      </div>

      {
        !eligibleQuestions.length &&
        <SectionHint>{'This assessment has no gradeable questions (checkbox, radio, or multitext with answer options).'}</SectionHint>
      }

      {
        eligibleQuestions.map(({ id, question, answerOptions = [] }) => {
          const correctIds = correctAnswers[String(id)] || [];
          return (
            <QuestionBlock key={id}>
              <QuestionText>{question || `Question ${id}`}</QuestionText>
              {
                answerOptions.map(({ id: optionId, answerOption }) => (
                  <Checkbox
                    key={optionId}
                    label={answerOption || `Option ${optionId}`}
                    value={correctIds.some(correctId => String(correctId) === String(optionId))}
                    onChange={() => {
                      if (!isReadOnly) {
                        onCorrectOptionToggled(id, optionId)
                      }
                    }}
                    disabled={isReadOnly}
                  />
                ))
              }
            </QuestionBlock>
          )
        })
      }

      {
        staleQuestionIds.map(questionId => (
          <WarningText key={questionId}>
            <FaExclamationTriangle style={{ color: '#EAC60C' }} />
            {`Correct answers are configured for a question (id ${questionId}) that no longer exists.`}
            {
              !isReadOnly &&
              <div
                style={{ color: '#0280c6', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => removeStaleQuestion(questionId)}
              >
                {'Remove it'}
              </div>
            }
          </WarningText>
        ))
      }

      <div>
        <SectionTitle>{'Percentage Ranges'}</SectionTitle>
        <SectionHint>
          {'The student\'s grade is the percentage of graded questions answered correctly. The first range containing the grade decides the followup assessment.'}
        </SectionHint>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
        {
          ranges.map((range, rangeIndex) => (
            <RangeEditor
              key={range.id || rangeIndex}
              range={range}
              assessmentNames={assessmentNames}
              modsByAssessment={modsByAssessment}
              currentAssessmentName={currentAssessmentName}
              onChange={(updatedRange) => onRangeChanged(rangeIndex, updatedRange)}
              onDelete={() => onRangeDeleted(rangeIndex)}
              isReadOnly={isReadOnly}
            />
          ))
        }
      </div>

      {
        !isReadOnly &&
        <Button style={{ display: 'inline-flex', alignSelf: 'flex-start' }} onClick={addNewRange}>
          <FaPlus style={{ fontSize: '10px', marginRight: '5px' }} />
          {'Add Range'}
        </Button>
      }
    </div>
  )
}
